import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

interface Category {
  id: number;
  name_ar: string;
  slug: string;
}

interface Subcategory {
  id: number;
  name_ar: string;
}

interface Region {
  id: number;
  name_ar: string;
}

interface City {
  id: number;
  name_ar: string;
}

interface AgeCategory {
  id: number;
  name_ar: string;
}

const CreateAdPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [ageCategories, setAgeCategories] = useState<AgeCategory[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [ageCategoryId, setAgeCategoryId] = useState('');
  const [price, setPrice] = useState('');
  const [priceType, setPriceType] = useState('fixed');
  const [quantity, setQuantity] = useState('1');
  const [gender, setGender] = useState('male');
  const [weight, setWeight] = useState('');
  const [ageMonths, setAgeMonths] = useState('');
  const [regionId, setRegionId] = useState('');
  const [cityId, setCityId] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [showPhone, setShowPhone] = useState(true);
  const [responsibilityAccepted, setResponsibilityAccepted] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    if (!token) {
      toast.error('يجب تسجيل الدخول أولاً');
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, regRes] = await Promise.all([
          api.get('/categories'),
          api.get('/regions'),
        ]);
        setCategories(catRes.data.data || []);
        setRegions(regRes.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (categoryId) {
      api.get(`/categories/${categoryId}/subcategories`).then((res) => {
        setSubcategories(res.data.data || []);
      }).catch(() => setSubcategories([]));

      api.get(`/categories/${categoryId}/age-categories`).then((res) => {
        setAgeCategories(res.data.data || []);
      }).catch(() => setAgeCategories([]));
    } else {
      setSubcategories([]);
      setAgeCategories([]);
      setSubcategoryId('');
      setAgeCategoryId('');
    }
  }, [categoryId]);

  useEffect(() => {
    if (regionId) {
      api.get(`/regions/${regionId}/cities`).then((res) => {
        setCities(res.data.data || []);
      }).catch(() => setCities([]));
    } else {
      setCities([]);
      setCityId('');
    }
  }, [regionId]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (images.length + files.length > 10) {
      toast.error('الحد الأقصى 10 صور');
      return;
    }
    setImages((prev) => [...prev, ...files]);
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !description || !categoryId || !price || !cityId) {
      toast.error('أكمل جميع الحقول المطلوبة');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('category_id', categoryId);
      if (subcategoryId) formData.append('subcategory_id', subcategoryId);
      if (ageCategoryId) formData.append('age_category_id', ageCategoryId);
      formData.append('price', price);
      formData.append('price_type', priceType);
      formData.append('quantity', quantity);
      formData.append('gender', gender);
      if (weight) formData.append('weight', weight);
      if (ageMonths) formData.append('age_months', ageMonths);
      formData.append('city_id', cityId);
      formData.append('contact_phone', contactPhone || user?.phone || '');
      formData.append('show_phone', showPhone ? '1' : '0');
      formData.append('disclaimer_accepted', '1');

      images.forEach((img, idx) => {
        formData.append(`images[${idx}]`, img);
      });

      const res = await api.post('/ads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success('تم إضافة الإعلان بنجاح');
      navigate(`/ads/${res.data.data?.id || ''}`);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const firstError = Object.values(errors)[0];
        toast.error(Array.isArray(firstError) ? firstError[0] as string : 'حدث خطأ');
      } else {
        toast.error(err.response?.data?.message || 'حدث خطأ');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) return null;

  return (
    <div className="max-w-site mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">إضافة إعلان جديد</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* المعلومات الأساسية */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">المعلومات الأساسية</h2>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">عنوان الإعلان *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="مثال: نعجة نجدية للبيع"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              maxLength={100}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">الوصف *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="اكتب وصف تفصيلي للإعلان..."
              rows={5}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              maxLength={2000}
            />
            <p className="text-xs text-gray-400 mt-1">{description.length}/2000</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">التصنيف *</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">اختر التصنيف</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name_ar}</option>
                ))}
              </select>
            </div>

            {subcategories.length > 0 && (
              <div>
                <label className="block text-sm text-gray-600 mb-2">السلالة</label>
                <select
                  value={subcategoryId}
                  onChange={(e) => setSubcategoryId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">اختر السلالة</option>
                  {subcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>{sub.name_ar}</option>
                  ))}
                </select>
              </div>
            )}

            {ageCategories.length > 0 && (
              <div>
                <label className="block text-sm text-gray-600 mb-2">فئة العمر</label>
                <select
                  value={ageCategoryId}
                  onChange={(e) => setAgeCategoryId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">اختر فئة العمر</option>
                  {ageCategories.map((age) => (
                    <option key={age.id} value={age.id}>{age.name_ar}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* المواصفات والسعر */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">المواصفات والسعر</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">السعر (ر.س) *</label>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">نوع السعر</label>
              <select
                value={priceType}
                onChange={(e) => setPriceType(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="fixed">ثابت</option>
                <option value="negotiable">قابل للتفاوض</option>
                <option value="auction">مزاد</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">الكمية (رأس)</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">الجنس</label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="male">ذكر</option>
                <option value="female">أنثى</option>
                <option value="mixed">مختلط</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">الوزن (كجم)</label>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="اختياري"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">العمر (شهر)</label>
              <input
                type="number"
                value={ageMonths}
                onChange={(e) => setAgeMonths(e.target.value)}
                placeholder="اختياري"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* الموقع */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">الموقع</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">المنطقة *</label>
              <select
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">اختر المنطقة</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>{r.name_ar}</option>
                ))}
              </select>
            </div>

            {cities.length > 0 && (
              <div>
                <label className="block text-sm text-gray-600 mb-2">المدينة *</label>
                <select
                  value={cityId}
                  onChange={(e) => setCityId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">اختر المدينة</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>{c.name_ar}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* التواصل */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">التواصل</h2>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">رقم التواصل</label>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value.replace(/\D/g, ''))}
              placeholder={user?.phone || '05XXXXXXXX'}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              dir="ltr"
              maxLength={10}
            />
            <p className="text-xs text-gray-400 mt-1">اتركه فاضي لاستخدام رقمك المسجل</p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showPhone}
              onChange={(e) => setShowPhone(e.target.checked)}
              className="w-4 h-4 text-primary-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-600">إظهار رقم الجوال في الإعلان</span>
          </label>
        </div>

        {/* إقرار المسؤولية */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={responsibilityAccepted}
              onChange={(e) => setResponsibilityAccepted(e.target.checked)}
              className="w-5 h-5 mt-0.5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-semibold text-gray-700">إقرار المسؤولية *</span>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                أقر بأن جميع المعلومات المدخلة صحيحة وأنني المسؤول الوحيد عن محتوى هذا الإعلان.
                وأتعهد بأن المواشي المعروضة سليمة وخالية من الأمراض المعدية.
              </p>
            </div>
          </label>
        </div>

        {/* الصور */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">الصور</h2>
          <p className="text-xs text-gray-400 mb-4">أضف حتى 10 صور. الصورة الأولى ستكون الصورة الرئيسية.</p>

          <div className="flex flex-wrap gap-3 mb-4">
            {previews.map((src, idx) => (
              <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                >
                  ✕
                </button>
                {idx === 0 && (
                  <span className="absolute bottom-0 left-0 right-0 bg-primary-500 text-white text-center text-xs py-0.5">رئيسية</span>
                )}
              </div>
            ))}

            {images.length < 10 && (
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 transition">
                <span className="text-2xl text-gray-400">+</span>
                <span className="text-xs text-gray-400">إضافة صورة</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* زر الإرسال */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-primary-500 text-white py-3.5 rounded-xl font-semibold hover:bg-primary-600 transition disabled:opacity-50 text-lg"
          >
            {loading ? 'جاري النشر...' : 'نشر الإعلان'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-8 bg-gray-100 text-gray-600 py-3.5 rounded-xl font-semibold hover:bg-gray-200 transition"
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAdPage;
